import React, { useCallback, useMemo, useState, useEffect } from 'react';
import ReactFlow, {
  Node,
  Edge,
  addEdge,
  ConnectionLineType,
  useNodesState,
  useEdgesState,
  MarkerType,
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  NodeProps
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Goal } from '../types';
import { Target, Brain, Gem, Clock, BarChart3, RefreshCw, Layers, ArrowRight, ArrowDown } from 'lucide-react';
import { getLayoutedElements, LayoutPresets } from '../utils/layoutUtils';

interface GoalGraphProps {
  goals: Goal[];
  onGoalClick: (goalId: string) => void;
}

// Custom Node Component for Goals
const GoalNode: React.FC<NodeProps> = ({ data }) => {
  const goal = data.goal as Goal;
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'border-green-500 bg-green-50';
      case 'in_progress': return 'border-blue-500 bg-blue-50';
      case 'blocked': return 'border-red-500 bg-red-50';
      case 'not_started': return 'border-gray-400 bg-white';
      default: return 'border-gray-400 bg-white';
    }
  };

  const getComplexityColor = (complexity?: number) => {
    if (!complexity) return 'text-gray-500';
    if (complexity <= 3) return 'text-green-600';
    if (complexity <= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getLevelColor = (level: number) => {
    switch (level) {
      case 0: return 'border-l-4 border-l-purple-500';
      case 1: return 'border-l-4 border-l-blue-500';
      case 2: return 'border-l-4 border-l-green-500';
      default: return 'border-l-4 border-l-gray-500';
    }
  };

  return (
    <div 
      className={`p-4 rounded-lg border-2 shadow-sm min-w-[250px] max-w-[300px] cursor-pointer hover:shadow-md transition-shadow ${getStatusColor(goal.status)} ${getLevelColor(goal.level)}`}
      onClick={() => data.onClick(goal.id)}
    >
      <Handle type="target" position={Position.Top} />
      
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-4 h-4 text-gray-600" />
        <h4 className="font-medium text-gray-900 text-sm leading-tight">
          {goal.name}
        </h4>
        
        <div className="flex gap-1 ml-auto">
          {goal.aiGenerated && (
            <Brain className="w-3 h-3 text-purple-500" />
          )}
          {goal.standalonePotential && (
            <Gem className="w-3 h-3 text-amber-500" />
          )}
        </div>
      </div>
      
      {goal.description && (
        <p className="text-xs text-gray-600 mb-2 line-clamp-2">
          {goal.description}
        </p>
      )}
      
      <div className="flex items-center gap-3 text-xs text-gray-500">
        <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getStatusColor(goal.status)}`}>
          Level {goal.level}
        </span>
        
        {goal.complexityScore && (
          <div className="flex items-center gap-1">
            <BarChart3 className="w-3 h-3" />
            <span className={getComplexityColor(goal.complexityScore)}>
              {goal.complexityScore}/10
            </span>
          </div>
        )}
        
        {goal.estimatedHours && (
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>{goal.estimatedHours}h</span>
          </div>
        )}
      </div>
      
      <Handle type="source" position={Position.Bottom} />
    </div>
  );
};

const nodeTypes = {
  goalNode: GoalNode,
};

export const GoalGraph: React.FC<GoalGraphProps> = ({ goals, onGoalClick }) => {
  const [layoutDirection, setLayoutDirection] = useState<'TB' | 'LR'>('TB');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Convert hierarchical goals to flat array
  const flatGoals = useMemo(() => {
    const flat: Goal[] = [];
    
    const traverse = (goalList: Goal[]) => {
      goalList.forEach(goal => {
        flat.push(goal);
        if (goal.children && goal.children.length > 0) {
          traverse(goal.children);
        }
      });
    };
    
    traverse(goals);
    return flat;
  }, [goals]);

  // Create and layout nodes and edges
  const { layoutedNodes, layoutedEdges } = useMemo(() => {
    // Create nodes for React Flow
    const initialNodes: Node[] = flatGoals.map((goal) => ({
      id: goal.id,
      type: 'goalNode',
      position: { x: 0, y: 0 }, // Will be set by layout
      data: { 
        goal,
        onClick: onGoalClick,
        level: goal.level
      },
    }));

    // Create edges for React Flow
    const initialEdges: Edge[] = [];
    
    flatGoals.forEach(goal => {
      if (goal.parentGoalId) {
        initialEdges.push({
          id: `${goal.parentGoalId}-${goal.id}`,
          source: goal.parentGoalId,
          target: goal.id,
          type: 'smoothstep',
          animated: true,
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
          style: {
            strokeWidth: 2,
            stroke: '#6366f1',
          },
        });
      }
    });

    // Apply auto-layout
    const preset = LayoutPresets.GOAL_HIERARCHY;
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      initialNodes,
      initialEdges,
      layoutDirection,
      preset.nodeWidth,
      preset.nodeHeight
    );

    return { layoutedNodes, layoutedEdges };
  }, [flatGoals, onGoalClick, layoutDirection]);

  // Update nodes and edges when layout changes
  useEffect(() => {
    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [layoutedNodes, layoutedEdges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: any) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const handleLayoutChange = useCallback((direction: 'TB' | 'LR') => {
    setLayoutDirection(direction);
  }, []);

  const handleAutoLayout = useCallback(() => {
    // Trigger re-layout by updating the layout direction state
    setLayoutDirection(prev => prev === 'TB' ? 'LR' : 'TB');
    setTimeout(() => setLayoutDirection(prev => prev === 'TB' ? 'LR' : 'TB'), 100);
  }, []);

  return (
    <div className="w-full border border-gray-200 rounded-lg overflow-hidden">
      {/* Layout Controls */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Target className="w-4 h-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Goal Layout:</span>
          </div>
          <div className="flex items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
            <button
              onClick={() => handleLayoutChange('TB')}
              className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                layoutDirection === 'TB'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ArrowDown className="w-3 h-3" />
              Hierarchical
            </button>
            <button
              onClick={() => handleLayoutChange('LR')}
              className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${
                layoutDirection === 'LR'
                  ? 'bg-blue-100 text-blue-700 border border-blue-200'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ArrowRight className="w-3 h-3" />
              Flow
            </button>
          </div>
        </div>
        <button
          onClick={handleAutoLayout}
          className="btn-secondary flex items-center gap-2 text-xs"
        >
          <Layers className="w-3 h-3" />
          Auto-Layout
        </button>
      </div>

      <div className="h-[700px]">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          connectionLineType={ConnectionLineType.SmoothStep}
          fitView
          fitViewOptions={{
            padding: 0.2,
            includeHiddenNodes: false,
          }}
          attributionPosition="top-right"
          nodesDraggable={true}
          nodesConnectable={false}
          elementsSelectable={true}
        >
          <Controls />
          <MiniMap 
            nodeStrokeWidth={3}
            nodeColor={(node) => {
              const goal = node.data.goal as Goal;
              switch (goal.level) {
                case 0: return '#8b5cf6';
                case 1: return '#3b82f6';
                case 2: return '#10b981';
                default: return '#6b7280';
              }
            }}
            className="!bg-gray-50"
          />
          <Background color="#aaa" gap={16} />
        </ReactFlow>
      </div>
    </div>
  );
};